import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createHead } from '@vueuse/head';
import { createAuth } from '@/features/auth/composables/useAuth';
import { createNavigation } from '@asabdev/asab-ui';
import '@asabdev/asab-ui/dist/style.css';
import './style.css';
import App from './App.vue';
import { router } from './router/router';
import { useAuthStore } from './stores/auth';
import { useOrganizationsStore } from './stores/organizations';
import { clickOutside } from './directives/click-outside';
import { NAVIGATION_ITEMS, ADMIN_NAVIGATION_ITEMS } from './router/types';
import { setupGlobalErrorHandler } from '@/lib/api/errorBoundary';

// Theme initialization function
function initializeTheme() {
  // Load theme from localStorage or default to 'light'
  const savedTheme = localStorage.getItem('userPreferences');
  let theme = 'light';

  if (savedTheme) {
    try {
      const preferences = JSON.parse(savedTheme);
      theme = preferences.theme || 'light';
    } catch (error) {
      console.warn('Failed to parse saved theme preference:', error);
    }
  } else {
    // Check system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    theme = prefersDark ? 'dark' : 'light';
  }

  // Apply theme to document
  document.documentElement.classList.toggle('dark', theme === 'dark');

  return theme;
}

// Initialize theme early
initializeTheme();

const app = createApp(App);
const pinia = createPinia();

app.directive('click-outside', clickOutside);
app.use(pinia);

// Initialize auth store after Pinia is ready
const authStore = useAuthStore();

// Create auth system adapter to match the expected interface
// The auth store properties are computed/getters, so we need to unwrap their values
const authStoreAdapter = {
  get user() {
    return authStore.user;
  },
  get isAuthenticated() {
    return authStore.isAuthenticated;
  },
  get isVerified() {
    return authStore.isVerified;
  },
  get isActive() {
    return authStore.isActive;
  },
  get isLoading() {
    return authStore.isLoading;
  },
  get error() {
    return authStore.error;
  },
  login: authStore.login,
  register: authStore.register,
  logout: authStore.logout,
  forgotPassword: authStore.forgotPassword,
  resetPassword: authStore.resetPassword,
  verifyEmail: authStore.verifyEmail,
  requestVerifyToken: authStore.requestVerifyToken,
  clearError: authStore.clearError,
  refreshSession: authStore.refreshSession,
};

const auth = createAuth(authStoreAdapter);

app.use(auth);
app.use(createHead());
app.use(router);

// Initialize auth store first
authStore
  .initialize()
  .then(async () => {
    // Initialize organizations if authenticated
    if (authStore.isAuthenticated) {
      const orgStore = useOrganizationsStore();
      await orgStore.initialize();
    }

    // Add a small delay to ensure auth state is fully settled
    setTimeout(() => {
      // Create navigation system after auth is initialized
      const navigation = createNavigation(router, auth, {
        main: NAVIGATION_ITEMS,
        admin: ADMIN_NAVIGATION_ITEMS,
      });

      app.use(navigation);

      // Setup global error handler after all plugins are configured
      setupGlobalErrorHandler(app);

      app.mount('#app');
    }, 100);
  })
  .catch(error => {
    console.error('Failed to initialize auth:', error);
    // Mount app anyway but without navigation
    setupGlobalErrorHandler(app);
    app.mount('#app');
  });
